import {
  companionUrl,
  ensureSession,
  resetCompanionSession,
} from './companionRequest';

const SAMPLE_RATE = 24000;
const TRANSCRIPT_TIMEOUT_MS = 15000;
// the agent accepts the socket first and closes with this code once it finds the cookie stale
const SESSION_EXPIRED_CLOSE_CODE = 4401;

export type VoiceRecorder = {
  stop: () => Promise<string | null>;
  discard: () => void;
};

const toPcm16 = (samples: Float32Array): ArrayBuffer => {
  const pcm = new Int16Array(samples.length);
  samples.forEach((sample, index) => {
    const clamped = Math.max(-1, Math.min(1, sample));
    pcm[index] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  });
  return pcm.buffer;
};

/** A frame that isn't the transcript must still settle the stop() promise. */
const readTranscript = (payload: unknown): string | null => {
  try {
    return JSON.parse(String(payload)).text ?? null;
  } catch {
    return null;
  }
};

const socketUrl = () => {
  const url = new URL(companionUrl('/api/audio'), window.location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
};

/** Resolves once frames can be sent, so a recording never starts on a connecting socket. */
const openSocket = (): Promise<WebSocket> =>
  new Promise((resolve, reject) => {
    const socket = new WebSocket(socketUrl());
    socket.binaryType = 'arraybuffer';
    socket.onopen = () => resolve(socket);
    socket.onerror = () =>
      reject(new Error('Companion audio socket failed to open'));
  });

/** Everything after the microphone is granted; a failure here must not leave the mic live. */
const connect = async (stream: MediaStream) => {
  try {
    // the socket rides the same cookie as REST calls but has no 401 → re-login path of its own
    await ensureSession();
    const context = new AudioContext({ sampleRate: SAMPLE_RATE });
    const socket = await openSocket().catch(error => {
      context.close();
      throw error;
    });
    return { context, socket };
  } catch (error) {
    stream.getTracks().forEach(track => track.stop());
    throw error;
  }
};

/** Streams microphone PCM to the agent and resolves the transcript on stop. */
export const startRecording = async (): Promise<VoiceRecorder> => {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const { context, socket } = await connect(stream);
  const source = context.createMediaStreamSource(stream);
  const processor = context.createScriptProcessor(4096, 1, 1);

  socket.addEventListener('close', event => {
    if (event.code === SESSION_EXPIRED_CLOSE_CODE) resetCompanionSession();
  });

  processor.onaudioprocess = event => {
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(toPcm16(event.inputBuffer.getChannelData(0)));
    }
  };
  source.connect(processor);
  // the processor only runs while connected to the destination, so route it through silence
  const silence = context.createGain();
  silence.gain.value = 0;
  processor.connect(silence);
  silence.connect(context.destination);

  const teardown = () => {
    processor.disconnect();
    source.disconnect();
    stream.getTracks().forEach(track => track.stop());
    context.close();
  };

  return {
    stop: () =>
      new Promise(resolve => {
        teardown();
        if (socket.readyState !== WebSocket.OPEN) {
          socket.close();
          resolve(null);
          return;
        }
        const timer = setTimeout(() => {
          socket.close();
          resolve(null);
        }, TRANSCRIPT_TIMEOUT_MS);
        socket.onmessage = event => {
          clearTimeout(timer);
          resolve(readTranscript(event.data));
        };
        socket.onclose = () => {
          clearTimeout(timer);
          resolve(null);
        };
        socket.send(JSON.stringify({ type: 'stop' }));
      }),
    discard: () => {
      teardown();
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'discard' }));
      }
      socket.close();
    },
  };
};
